'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useRouter } from 'next/navigation';

interface UserProfile {
  id: string;
  company_id: string | null;
  branch_id: string | null;
  email: string;
  full_name: string;
  full_name_ar: string | null;
  role: string;
  phone: string | null;
  is_active: boolean;
}

// Role-based permission map — complete RBAC matrix
// NOTE: 'admin' role = Company Manager — manages their own company only
// Platform-level access is exclusively for 'super_admin' and platform_* roles
export const ROLE_PERMISSIONS: Record<string, string[]> = {
  super_admin: ['*'],
  admin: [
    'dashboard', 'pos', 'pos_checkout', 'pos_transactions', 'inventory', 'products',
    'customers', 'customer_management', 'suppliers', 'purchases', 'returns',
    'accounting', 'employees', 'reports', 'branches', 'settings', 'cash_management',
    'approvals', 'audit_logs', 'backup', 'csv_import', 'invoice_history',
    'invoice_archive', 'barcode_labels',
    // NOTE: 'super_admin' permission intentionally excluded — admin = company manager only
  ],
  manager: [
    'dashboard', 'pos', 'pos_checkout', 'pos_transactions', 'inventory', 'products',
    'customers', 'customer_management', 'suppliers', 'purchases', 'returns',
    'accounting', 'employees', 'reports', 'branches', 'cash_management',
    'approvals', 'invoice_history', 'invoice_archive', 'barcode_labels',
  ],
  accountant: [
    'dashboard', 'accounting', 'reports', 'purchases', 'returns', 'cash_management',
    'customers', 'suppliers', 'invoice_history', 'invoice_archive',
  ],
  cashier: [
    'pos', 'pos_checkout', 'pos_transactions', 'customers', 'customer_management',
    'invoice_history',
  ],
  viewer: ['dashboard', 'reports'],
  employee: ['dashboard'],
  vendor: ['dashboard'],
  warehouse_user: ['dashboard', 'inventory', 'products', 'purchases', 'returns'],
  hr_user: ['dashboard', 'employees'],
  platform_support: ['super_admin'],
  platform_finance: ['super_admin'],
  platform_viewer: ['super_admin'],
};

export const hasPermission = (role: string, permission: string): boolean => {
  const perms = ROLE_PERMISSIONS[role] || [];
  return perms.includes('*') || perms.includes(permission);
};

// Demo user definitions — view-only accounts for platform demonstration
export const DEMO_USERS = [
  {
    email: 'admin@nexora.sa',
    password: 'NexoraAdmin@2026',
    role: 'admin',
    name: 'مدير الشركة التجريبية',
    nameEn: 'Demo Company Manager',
    description: 'إدارة شاملة لشركتك — المستخدمين والفروع والإعدادات والتقارير والعمليات اليومية',
  },
  {
    email: 'manager@nexora.sa',
    password: 'NexoraStore@2026',
    role: 'manager',
    name: 'مدير المتجر التجريبي',
    nameEn: 'Demo Store Manager',
    description: 'إدارة العمليات اليومية — المبيعات والمشتريات والمخزون والموظفين',
  },
  {
    email: 'cashier@nexora.sa',
    password: 'NexoraCash@2026',
    role: 'cashier',
    name: 'كاشير تجريبي',
    nameEn: 'Demo Cashier',
    description: 'نقطة البيع والمعاملات النقدية وفواتير العملاء',
  },
  {
    email: 'accounts@nexora.sa',
    password: 'NexoraAcc@2026',
    role: 'accountant',
    name: 'محاسب تجريبي',
    nameEn: 'Demo Accountant',
    description: 'المحاسبة والتقارير المالية وضريبة القيمة المضافة والمشتريات',
  },
];

// Platform owner — always granted super_admin regardless of DB state
export const PLATFORM_OWNER_EMAIL = 'abomozan2023@gmail.com';

// All platform owner emails (current + legacy)
const PLATFORM_OWNER_EMAILS = [
  'abomozan2023@gmail.com',
  'abomozan2011@gmail.com',
];

// Platform owner known password (used as fallback if Supabase auth fails)
const PLATFORM_OWNER_PASSWORD = 'Mm11022009';

export function isPlatformOwner(email: string): boolean {
  return PLATFORM_OWNER_EMAILS.some(e => e.toLowerCase() === email.toLowerCase());
}

export function isDemoEmail(email: string): boolean {
  return DEMO_USERS.some(u => u.email.toLowerCase() === email.toLowerCase());
}

export function findDemoUser(email: string, password: string) {
  return DEMO_USERS.find(
    u => u.email.toLowerCase() === email.toLowerCase() && u.password === password
  ) || null;
}

const DEMO_SESSION_KEY = 'nexora_demo_session';
const DEMO_COOKIE_NAME = 'nexora_demo_active';
// 24-hour expiry for demo sessions (default)
const DEMO_SESSION_TTL = 24 * 60 * 60 * 1000;
// 30-day expiry when "remember me" is checked
const DEMO_SESSION_TTL_LONG = 30 * 24 * 60 * 60 * 1000;

const REMEMBER_ME_KEY = 'nexora_remember_me';
const SESSION_ONLY_KEY = 'nexora_session_only';

export function saveDemoSession(demoUser: typeof DEMO_USERS[0]) {
  if (typeof window === 'undefined') return;
  const rememberMe = localStorage.getItem(REMEMBER_ME_KEY) === 'true';
  const ttl = rememberMe ? DEMO_SESSION_TTL_LONG : DEMO_SESSION_TTL;
  const payload = {
    email: demoUser.email,
    role: demoUser.role,
    name: demoUser.name,
    ts: Date.now(),
    ttl,
  };
  try {
    localStorage.setItem(DEMO_SESSION_KEY, JSON.stringify(payload));
  } catch {}
  // Cookie for middleware
  document.cookie = `${DEMO_COOKIE_NAME}=1; Path=/; SameSite=Lax; Max-Age=${ttl / 1000}`;
}

export function clearDemoSession() {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(DEMO_SESSION_KEY);
    // Also clear old key name for backwards compat
    localStorage.removeItem('demo_session');
  } catch {}
  document.cookie = `${DEMO_COOKIE_NAME}=; Path=/; SameSite=Lax; Max-Age=0`;
}

function loadDemoSession(): UserProfile | null {
  if (typeof window === 'undefined') return null;
  try {
    // Try new key first, then old key for backwards compat
    const raw = localStorage.getItem(DEMO_SESSION_KEY) || localStorage.getItem('demo_session');
    if (!raw) return null;
    const data = JSON.parse(raw);
    const ttl = data.ttl || DEMO_SESSION_TTL;
    if (Date.now() - data.ts > ttl) {
      clearDemoSession();
      return null;
    }
    return {
      id: 'demo-' + data.role,
      company_id: 'demo-company',
      branch_id: null,
      email: data.email,
      full_name: data.name || data.email,
      full_name_ar: data.name || null,
      role: data.role || 'cashier',
      phone: null,
      is_active: true,
    };
  } catch {
    return null;
  }
}

interface AuthContextType {
  user: { id: string; email?: string; email_confirmed_at?: string | null } | null;
  session: { user: { id: string; email?: string } } | null;
  profile: UserProfile | null;
  loading: boolean;
  isDemoUser: boolean;
  isViewOnly: boolean;
  signUp: (email: string, password: string, metadata?: Record<string, string>) => Promise<{ user: unknown; session: unknown }>;
  signIn: (email: string, password: string) => Promise<{ user: unknown; session: unknown }>;
  signInDemo: (email: string, password: string) => boolean;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  getCurrentUser: () => Promise<unknown>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<UserProfile | null>;
  refreshProfile: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<{ id: string; email?: string; email_confirmed_at?: string | null } | null>(null);
  const [session, setSession] = useState<{ user: { id: string; email?: string } } | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDemoUser, setIsDemoUser] = useState(false);
  const supabase = createClient();
  const router = useRouter();

  // View-only: demo users are always view-only
  const isViewOnly = isDemoUser;

  const fetchProfile = async (userId: string) => {
    try {
      // ── Fast path: get the current auth user first (single call, always works) ──
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const userEmail = authUser?.email || '';

      // ── Platform owner: NEVER depend on DB — construct profile instantly ──
      if (isPlatformOwner(userEmail)) {
        const ownerProfile: UserProfile = {
          id: userId,
          company_id: authUser?.user_metadata?.company_id || null,
          branch_id: null,
          email: userEmail,
          full_name: authUser?.user_metadata?.full_name || 'Platform Owner',
          full_name_ar: authUser?.user_metadata?.full_name || 'مالك المنصة',
          role: 'super_admin',
          phone: null,
          is_active: true,
        };
        setProfile(ownerProfile);
        // Fire-and-forget: ensure DB row exists (doesn't block login)
        supabase.rpc('ensure_user_profile', {
          p_user_id: userId,
          p_email: userEmail,
          p_full_name: ownerProfile.full_name,
          p_role: 'super_admin',
          p_company_id: null,
        }).then(() => {}).catch(() => {});
        return;
      }

      // ── Normal users: try DB first ──
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (!error && data) {
        const profileData = data as UserProfile;
        setProfile(profileData);
        // Update last_login silently
        supabase
          .from('user_profiles')
          .update({ last_login: new Date().toISOString() })
          .eq('id', userId)
          .then(() => {});
        return;
      }

      // ── Profile missing: use ensure_user_profile RPC (SECURITY DEFINER, bypasses RLS) ──
      if (error?.code === 'PGRST116' || error?.code === '406') {
        // Call the server-side function to create the profile
        await supabase.rpc('ensure_user_profile', {
          p_user_id: userId,
          p_email: userEmail,
          p_full_name: authUser?.user_metadata?.full_name || userEmail,
          p_role: authUser?.user_metadata?.role || 'admin',
          p_company_id: authUser?.user_metadata?.company_id || null,
        });

        // Now fetch the freshly created profile
        const { data: newData, error: newError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', userId)
          .single();

        if (!newError && newData) {
          setProfile(newData as UserProfile);
          return;
        }

        // Last resort: build minimal profile from auth metadata so user can access app
        if (authUser?.email) {
          const minimalProfile: UserProfile = {
            id: userId,
            company_id: authUser.user_metadata?.company_id || null,
            branch_id: null,
            email: authUser.email,
            full_name: authUser.user_metadata?.full_name || authUser.email,
            full_name_ar: authUser.user_metadata?.full_name || null,
            role: authUser.user_metadata?.role || 'admin',
            phone: null,
            is_active: true,
          };
          setProfile(minimalProfile);
        }
      }
    } catch {
      // Profile fetch failed — user may not have a profile yet
    }
  };

  useEffect(() => {
    // If session-only mode (remember me was off), clear session on new browser tab/window
    // sessionStorage is cleared when the browser tab is closed
    if (typeof window !== 'undefined') {
      const isSessionOnly = sessionStorage.getItem(SESSION_ONLY_KEY);
      const rememberMe = localStorage.getItem(REMEMBER_ME_KEY) === 'true';
      if (!rememberMe && !isSessionOnly) {
        // First visit in this browser session without remember me — check if we should clear
        // We only clear if this is a fresh browser session (no sessionStorage marker)
        const hasSessionMarker = sessionStorage.getItem('nexora_active_session');
        if (!hasSessionMarker) {
          // New browser session and remember me is off — sign out
          const demoRaw = localStorage.getItem(DEMO_SESSION_KEY);
          if (demoRaw) {
            try {
              const demoData = JSON.parse(demoRaw);
              // Only clear if it was a non-remember-me session
              if (!demoData.rememberMe) {
                clearDemoSession();
              }
            } catch {}
          }
        }
      }
      // Mark this as an active session
      try { sessionStorage.setItem('nexora_active_session', '1'); } catch {}
    }

    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      setSession(currentSession);
      setUser(currentSession?.user ?? null);

      if (currentSession?.user) {
        // Check if the authenticated user is a demo user
        const isDemo = isDemoEmail(currentSession.user.email || '');
        setIsDemoUser(isDemo);
        fetchProfile(currentSession.user.id).then(() => setLoading(false));
      } else {
        // No Supabase session — check for demo session in localStorage
        const demoProfile = loadDemoSession();
        if (demoProfile) {
          setProfile(demoProfile);
          setUser({ id: demoProfile.id, email: demoProfile.email });
          setIsDemoUser(true);
        }
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      if (newSession?.user) {
        const isDemo = isDemoEmail(newSession.user.email || '');
        setIsDemoUser(isDemo);
        fetchProfile(newSession.user.id);
      } else {
        // Only clear profile if no demo session
        const demo = loadDemoSession();
        if (demo) {
          setProfile(demo);
          setUser({ id: demo.id, email: demo.email });
          setIsDemoUser(true);
        } else {
          setProfile(null);
          setIsDemoUser(false);
        }
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, metadata: Record<string, string> = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          full_name_ar: metadata?.fullNameAr || '',
          role: metadata?.role || 'cashier',
        },
        emailRedirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    if (data.user) {
      const isDemo = isDemoEmail(data.user.email || '');
      setIsDemoUser(isDemo);
      await fetchProfile(data.user.id);
    }
    return data;
  };

  /**
   * Sign in as a demo user (no Supabase auth required).
   * Returns true if credentials match a known demo user.
   */
  const signInDemo = (email: string, password: string): boolean => {
    const demoUser = findDemoUser(email, password);
    if (!demoUser) return false;
    saveDemoSession(demoUser);
    const demoProfile: UserProfile = {
      id: 'demo-' + demoUser.role,
      company_id: 'demo-company',
      branch_id: null,
      email: demoUser.email,
      full_name: demoUser.name,
      full_name_ar: demoUser.name,
      role: demoUser.role,
      phone: null,
      is_active: true,
    };
    setProfile(demoProfile);
    setUser({ id: demoProfile.id, email: demoProfile.email });
    setIsDemoUser(true);
    return true;
  };

  const signOut = async () => {
    // Clear demo session
    clearDemoSession();
    setIsDemoUser(false);
    // Clear session markers
    try {
      sessionStorage.removeItem(SESSION_ONLY_KEY);
      sessionStorage.removeItem('nexora_active_session');
    } catch {}
    try {
      await supabase.auth.signOut();
    } catch {
      // ignore signout errors
    }
    setProfile(null);
    setUser(null);
    setSession(null);
    router.push('/sign-up-login-screen');
    router.refresh();
  };

  const resetPassword = async (email: string) => {
    const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || window.location.origin;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${siteUrl}/auth/callback?next=/reset-password`,
    });
    if (error) throw error;
  };

  const getCurrentUser = async () => {
    const { data: { user: currentUser }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return currentUser;
  };

  const isEmailVerified = () => {
    return user?.email_confirmed_at != null;
  };

  const getUserProfile = async (): Promise<UserProfile | null> => {
    if (!user) return null;
    if (isDemoUser) return profile;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data as UserProfile;
  };

  const refreshProfile = async () => {
    if (isDemoUser) return; // demo profiles don't need refresh
    if (user && !user.id.startsWith('demo-')) await fetchProfile(user.id);
  };

  const checkPermission = (permission: string): boolean => {
    if (!profile) return false;
    return hasPermission(profile.role, permission);
  };

  const value: AuthContextType = {
    user,
    session,
    profile,
    loading,
    isDemoUser,
    isViewOnly,
    signUp,
    signIn,
    signInDemo,
    signOut,
    resetPassword,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    refreshProfile,
    hasPermission: checkPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
